import React, {useEffect, useState} from 'react';
import { Helmet } from 'react-helmet-async';
import {Outlet, useNavigate, useParams} from 'react-router-dom';
import styled from 'styled-components';
import { t } from 'ttag';
import vocab from '../../../config/channels';
import { operatorName } from '../../../config/operator';
import { TChannel } from '../../../contexts/operator/operator-context';
import useOperatorContext from '../../../contexts/operator/useOperatorContext';
import {MenuData, TSite} from '../../../contexts/site/site-context';
import useSiteContext from '../../../contexts/site/useSiteContext';
import PageSpinnerThree from '../../general/PageSpinnerThree';
import AppLayout from '../../layouts/app/AppLayout';
import useBasketContext from "../../../contexts/basket/useBasketContext";
import {iLog} from "../../../index";
import {Form, Nav} from "react-bootstrap";
import {CTA} from "../../general/Button";
import config from "../../../config/channels";
import powered_by from "../../../assets/images/OrderswiftLogo2025.png";
import {getMenu} from "../../../services/api/site";
import {APIErrorCode} from "../../../services/api";

const TableSelectPage = () => {
    const operatorContext = useOperatorContext();
    const siteContext = useSiteContext();
    const basketContext = useBasketContext();
    const navigate = useNavigate();

    const [tableNumber, setTableNumber] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [site, setSite] = useState<TSite|undefined>(undefined);
    const [showErr, setShowErr] = useState<boolean>(false);

    const { restaurant_id } = useParams();

    useEffect(() => {
        basketContext.clearBasket();
        basketContext.setTableNumber(null);
        localStorage.removeItem('postcode');
        localStorage.removeItem('delivery-venue');

        if (siteContext) {
            let flow = operatorContext.operator?.flow ?? 'noflow';
            siteContext.fetchSites(operatorName, flow);
        } else {
            console.warn('Skipped fetch site call.');
        }
    }, []);

    useEffect(() => {
        if (siteContext.sites) {
            let found_site = siteContext.sites?.find(
                (site)=> site.handle === restaurant_id);

            if (found_site) {
                setSite(found_site);
            } else {
                navigate(`/${config.dineIn.handle}`);
            }
        }
    }, [siteContext.sites])

    const handleTableStart = () => {
        setShowErr(false);

        basketContext.setTableNumber(tableNumber);

        getMenu(operatorName, restaurant_id ?? '', 'dine_in', operatorContext.operator?.flow ?? '', tableNumber)
            .then((data: MenuData) => {
                localStorage.setItem('table', tableNumber);
                navigate(`/${config.dineIn.handle}/${site?.handle}`);
            })
            .catch((error) => {
                iLog('Issue fetching menu', [error.code, error.code && [APIErrorCode.TableNotFound].includes(error.code)]);
                if (error.code && [APIErrorCode.TableNotFound].includes(error.code)) {
                    setShowErr(true);
                } else {
                    navigate('/404');
                }
            });
    }

    if (!siteContext || !siteContext.sites) {
        return <PageSpinnerThree show={true} />;
    }

    return (
        <AppLayout page="order-at-table" dineInSite={site}>
            <Helmet>
                <title>
                    {operatorContext.operator?.name ?? ''} {t`Online Ordering - Powered by Orderswift`}
                </title>
                <link rel="icon" href={operatorContext.operator?.theme?.v4.favicon ?? ''} />
            </Helmet>

            <TableSelectWrapper>
                <StyledContainer className={'table-select-container'}>
                    { operatorContext?.operator?.theme?.v4?.slogan && (
                      <Heading className="slogan text-left"
                              dangerouslySetInnerHTML={{ __html: operatorContext?.operator?.theme.v4.slogan ?? '' }} />
                    )}

                    <ChannelContent>
                        <span className='d-md-none mobile_bg' />
                        <div className='channelWrapper'>
                            <StyledForm>
                                <InputWrapper controlId={'formSearch'}>
                                    <Form.Control
                                        size="lg"
                                        type="text"
                                        inputMode="numeric"
                                        className="table-input"
                                        placeholder={t`Enter table number`}
                                        value={tableNumber}
                                        onChange={(e) => setTableNumber(e.target.value)}
                                        autoFocus
                                    />

                                    { showErr && (
                                        <ErrorContainer>
                                            <span className="material-symbols-outlined">info</span>
                                            Sorry, the table number is not valid.
                                        </ErrorContainer>
                                    ) }

                                    <CTA className="table-input-cta"
                                         block
                                         disabled={!tableNumber || loading}
                                         loading={loading}
                                         onClick={handleTableStart}>
                                        <span>{t`View menu`}</span>
                                    </CTA>
                                </InputWrapper>
                            </StyledForm>
                        </div>
                    </ChannelContent>
                </StyledContainer>

                <div className='orderswift-banner'>
                    <img width={169} height={15} src={powered_by} alt={'Powered by Orderswift'} />
                </div>

            </TableSelectWrapper>
            <Outlet />
        </AppLayout>
    );
};

const TableSelectWrapper = styled.div`
    display: flex;
    justify-content: center;
    padding: calc(48px + 65px) 48px 48px 48px;

    @media screen and (max-width: 767px) {
        padding: calc(32px + 65px) 20px 48px 20px;
        margin-top: 0;
    }

    @media screen and (min-width: 768px) {
        height: 100vh;
    }

    .orderswift-banner {
        z-index: 999;
        display: flex;
        justify-content: center;
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        border-top: 2px solid #000000;
        padding: 10px 0;
    }
`;

const StyledContainer = styled.div`
    width: 100%;
    max-width: 600px;
    @media screen and (max-width: 767px){
        width: 100%;
    }
`;

const ErrorContainer = styled.div`
    color: var(--invalid);
    padding-top: 8px;
    display: flex;
    align-items: flex-start;
    img { margin-right: 12px; }

    & .material-symbols-outlined {
      margin-right: 8px;
      font-size: 24px;
      line-height: 24px;
    }

    margin-bottom: 20px;
`;

const StyledForm = styled(Form.Group)`
    padding-bottom: 16px;
`;

const InputWrapper = styled(Form.Group)`
    position: relative;

    .site-filter-input {
        height: 50px;
        &::placeholder { color:var(--placeholder); }
    }

    .form-label {
        font-size: 16px;
        line-height: 24px;
        color: var(--text-black);
        margin-bottom: 8px;
    }

    .form-control {
        text-align: center;
        padding: 18px 48px 18px 16px;
        padding-right: 30px;

        @media screen and (max-width: 767px) {
            background-position: 17px 12px, right 19px center;
        }
    }

  .table-input-cta {
    height: 48px;
    padding-top: 18px;
    padding-bottom: 18px;
      margin-top: 20px;
  }
`;

const ChannelContent = styled.div`
    flex-grow: 1;
    margin-top: 20px;
`;

const Heading = styled.h1`
    margin-bottom: 24px;
    font-size: 32px;
    font-weight: bold;
    line-height: 40px;
    text-wrap: pretty;

    @media screen and (max-width: 767px) {
        margin-top: 32px;
        margin-bottom: 20px;
    }
`;

export default TableSelectPage;
